import React, {
  createContext, useCallback, useContext, useMemo, useRef, useState,
} from 'react';
import { Alert, Snackbar } from '@mui/material';

type ToastState = 'idle' | 'saving' | 'saved' | 'error';

interface SaveToastContextValue {
  notifySaving: () => void;
  notifySaved: () => void;
  // Accepts an optional message for server-side errors that have
  // specific user-facing meaning (e.g. "User cannot change historical
  // data"). Falls back to the generic connection-error message.
  notifyError: (message?: string) => void;
}

const SaveToastContext = createContext<SaveToastContextValue>({
  notifySaving: () => {},
  notifySaved: () => {},
  notifyError: () => {},
});

const SAVING_SAFETY_TIMEOUT_MS = 15000;

export const SaveToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toastState, setToastState] = useState<ToastState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pendingCount = useRef(0);
  const hasError = useRef(false);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const safetyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDismissTimer = () => {
    if (dismissTimer.current) {
      clearTimeout(dismissTimer.current);
      dismissTimer.current = null;
    }
  };

  const clearSafetyTimer = () => {
    if (safetyTimer.current) {
      clearTimeout(safetyTimer.current);
      safetyTimer.current = null;
    }
  };

  const scheduleHide = useCallback(() => {
    clearDismissTimer();
    clearSafetyTimer();
    dismissTimer.current = setTimeout(() => {
      setToastState('idle');
      setErrorMessage(null);
      hasError.current = false;
    }, 3000);
  }, []);

  // If a caller fails to pair notifySaving with notifySaved/notifyError,
  // force-reset after a generous window so the toast never gets stuck.
  const scheduleSafetyReset = useCallback(() => {
    clearSafetyTimer();
    safetyTimer.current = setTimeout(() => {
      pendingCount.current = 0;
      setToastState(hasError.current ? 'error' : 'idle');
      hasError.current = false;
    }, SAVING_SAFETY_TIMEOUT_MS);
  }, []);

  const notifySaving = useCallback(() => {
    clearDismissTimer();
    pendingCount.current += 1;
    setToastState('saving');
    scheduleSafetyReset();
  }, [scheduleSafetyReset]);

  const notifySaved = useCallback(() => {
    pendingCount.current = Math.max(0, pendingCount.current - 1);
    if (pendingCount.current === 0) {
      setToastState(hasError.current ? 'error' : 'saved');
      scheduleHide();
    }
  }, [scheduleHide]);

  const notifyError = useCallback((message?: string) => {
    hasError.current = true;
    if (message) setErrorMessage(message);
    pendingCount.current = Math.max(0, pendingCount.current - 1);
    if (pendingCount.current === 0) {
      setToastState('error');
      scheduleHide();
    }
  }, [scheduleHide]);

  const contextValue = useMemo(
    () => ({ notifySaving, notifySaved, notifyError }),
    [notifySaving, notifySaved, notifyError],
  );

  return (
    <SaveToastContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={toastState !== 'idle'}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={toastState === 'error' ? 'error' : toastState === 'saving' ? 'info' : 'success'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toastState === 'saving' && 'Saving…'}
          {toastState === 'saved' && 'Saved'}
          {toastState === 'error' && (errorMessage || 'Error saving — check your connection')}
        </Alert>
      </Snackbar>
    </SaveToastContext.Provider>
  );
};

export const useSaveToast = () => useContext(SaveToastContext);
